import { useDefinedContext } from '@shared/hooks/useDefinedContext';
import { useIsOpen } from '@shared/hooks/useIsOpen';
import { useOverlay } from '@shared/hooks/useOverlay';
import { useQueryClient } from '@tanstack/react-query';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import type { ReactNode } from 'react';
import { createContext, useMemo, useState } from 'react';

interface BaseAvailabilityData {
  date: string;
  guestCount: number;
  listingPrice: number;
  time: string;
}

export interface MultiTableAvailabilityData extends BaseAvailabilityData {
  duration: number;
  multiTable: true;
  tableIds: string[];
  listingId?: string;
}

export interface SingleTableAvailabilityData extends BaseAvailabilityData {
  multiTable?: false;
  listingId: string;
  tableIds?: string[];
  isBestAvailable: boolean;
}

export type AvailabilityData =
  | MultiTableAvailabilityData
  | SingleTableAvailabilityData;

interface AvailabilityDrawerContextProviderProps {
  children: ReactNode;
}

export interface AvailabilityDrawerContextState {
  closeDrawer: () => void;
  availabilityData?: AvailabilityData;
  isOpen: boolean;
  openDrawer: (data: AvailabilityData) => void;
}

const AvailabilityDrawerContext =
  createContext<AvailabilityDrawerContextState | null>(null);
AvailabilityDrawerContext.displayName = 'AvailabilityDrawer';

export const useAvailabilityDrawerContext = () =>
  useDefinedContext(AvailabilityDrawerContext);

export const AvailabilityDrawerContextProvider = ({
  children,
}: AvailabilityDrawerContextProviderProps) => {
  const { id: restaurantId } = useRestaurant();
  const queryClient = useQueryClient();
  const {
    isOpen: isOpenDrawer,
    open: _openDrawer,
    close: _closeDrawer,
  } = useIsOpen();
  const [availabilityData, setAvailabilityData] = useState<AvailabilityData>();

  useOverlay(isOpenDrawer);

  const closeDrawer = (): void => {
    _closeDrawer();
    // Refresh occupants data when drawer closes
    queryClient.invalidateQueries({
      queryKey: ['restaurant', restaurantId, 'occupants'],
    });
  };

  const openDrawer = (newData: AvailabilityData): void => {
    _openDrawer();
    setAvailabilityData(newData);
  };

  const value = useMemo<AvailabilityDrawerContextState>(
    () => ({
      closeDrawer,
      availabilityData,
      isOpen: isOpenDrawer,
      openDrawer,
    }),
    [isOpenDrawer],
  );

  return (
    <AvailabilityDrawerContext.Provider value={value}>
      {children}
    </AvailabilityDrawerContext.Provider>
  );
};
